import { Fragment } from 'react';
import { tv } from 'tailwind-variants';
import { COMPANY } from '@/shared/config';
import { useNavigationGroups, type SidebarVariant } from '../model';
import { SidebarNavigationGroup } from './SidebarNavigationGroup';
import { SidebarPrimeBlock } from './SidebarPrimeBlock';
import { SidebarLastLogin } from './SidebarLastLogin';

const styles = tv({
  slots: {
    container:
      'flex flex-col h-full bg-tertiary-light dark:bg-secondary-bg gap-[2.4rem]',
    logo: 'flex items-center gap-[0.8rem] h-[3.6rem] shrink-0',
    logoImage: 'size-[3.6rem] shrink-0',
    logoText: 'h-[1.5rem] w-auto',
    groups:
      'flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-hide flex flex-col gap-[1.6rem]',
    divider: 'size-[0.3rem] rounded-full bg-separator shrink-0',
    footer: 'flex flex-col gap-[1rem] p-[0.4rem]',
  },
  variants: {
    variant: {
      expanded: {
        container: 'pt-[1.6rem] px-[1rem] pb-[1rem] border-r border-stroke',
      },
      collapsed: {
        container:
          'pt-[1.6rem] px-0 pb-[1rem] items-center border-r border-stroke',
        logo: 'px-0 justify-center',
        groups: 'items-center w-full',
        footer: 'items-center',
      },
      mobile: {
        container:
          'h-auto max-h-[calc(100dvh-7.2rem)] rounded-[1.2rem] p-[0.8rem] gap-[1.6rem] bg-background/90 backdrop-blur-[4px] border border-stroke shadow-[0_0.4rem_1.6rem_0_rgba(0,0,0,0.08)]',
        footer: 'px-[0.8rem]',
      },
    },
  },
});

type Props = {
  variant: SidebarVariant;
  onItemClick?: () => void;
};

const SidebarNavigation = ({ variant, onItemClick }: Props) => {
  const { groups, pathname } = useNavigationGroups();
  const collapsed = variant === 'collapsed';
  const mobile = variant === 'mobile';
  const classes = styles({ variant });

  return (
    <div className={classes.container()}>
      {!mobile && (
        <div className={classes.logo()}>
          <img
            src="/assets/f2ex-logo-small.svg"
            alt={COMPANY.name}
            className={classes.logoImage()}
          />
          {!collapsed && (
            <img
              src="/assets/f2ex-logo-full.svg"
              alt={COMPANY.name}
              className={classes.logoText()}
            />
          )}
        </div>
      )}

      <div className={classes.groups()}>
        {groups.map((group, index) => (
          <Fragment key={group.key}>
            {collapsed && index > 0 && <div className={classes.divider()} />}
            <SidebarNavigationGroup
              group={group}
              pathname={pathname}
              collapsed={collapsed}
              onItemClick={onItemClick}
            />
          </Fragment>
        ))}
      </div>

      <div className={classes.footer()}>
        {!mobile && <SidebarPrimeBlock collapsed={collapsed} />}
        {!collapsed && <SidebarLastLogin />}
      </div>
    </div>
  );
};

export { SidebarNavigation };
